import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import { useMemo, useState, useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { S3 } from "../../../../constants";
import FilterChips from "../../../athlete-portal/calendar/FilterChips";
import SvgIcon from "../../../../parts/SvgIcon";
import { RillContext } from "../../../../rill/RillReact/share/base";
import Modal from "../../../../parts/modal-new";

function getInitialOptions(locals) {
  const {
    activeUser: { athletes } = {},
    allAthletesFromOrganization,
    editCycle,
  } = locals;

  const teamMembers = editCycle?.members || [];
  const existingMemberIds = new Set(
    teamMembers.map((m) => String(m.id ?? m._id)).filter(Boolean)
  );

  // Team Coach added literally rather than migrated to canViewAllOrganizationClients: that
  // predicate also includes Nutritionist, which this picker (mirrored in
  // MassMessageModal.js's getInitialOptions) has never offered "all organization clients" to.
  const isAllowToViewAllOrganizationsAthletes =
    locals.organizationRoleName &&
    (locals.organizationRoleName === "Owner" ||
      locals.organizationRoleName === "Admin" ||
      locals.organizationRoleName === "Team Coach");

  const referAsClients = Boolean(locals.activeUser?.referAsClients);

  const athletesOptions = [];
  (athletes || [])
    .filter(({ status }) => !status?.inactive)
    .filter((athlete) => !existingMemberIds.has(String(athlete._id)))
    .forEach((athlete) => {
      athletesOptions.push({
        value: athlete._id,
        label: athlete.name,
        groups: athlete.groups || [],
        avatar: athlete.avatar,
      });
    });

  const allAthletesFromOrganizationOptions = [];
  if (allAthletesFromOrganization && isAllowToViewAllOrganizationsAthletes) {
    const uniqueAthleteIds = new Set();
    allAthletesFromOrganization
      .filter(({ athleteStatus }) => !athleteStatus?.inactive)
      .filter(({ athleteId }) => !existingMemberIds.has(String(athleteId)))
      .forEach(({ athleteId, athleteName, athleteGroups, athleteAvatar }) => {
        if (!uniqueAthleteIds.has(athleteId)) {
          uniqueAthleteIds.add(athleteId);
          allAthletesFromOrganizationOptions.push({
            value: athleteId,
            label: athleteName,
            groups: athleteGroups || [],
            avatar: athleteAvatar,
          });
        }
      });
  }

  return {
    athletesOptions,
    allAthletesFromOrganizationOptions,
    isAllowToViewAllOrganizationsAthletes,
    referAsClients,
  };
}

function TeamInviteClients(props) {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const context = useContext(RillContext);
  const locals = props.locals || context?.locals || {};
  const initialOptions = useMemo(() => getInitialOptions(locals), [locals]);

  const query = locals?.query || {};
  const pendingRecipients = query?.recipients;
  const pendingProgram = query?.program;

  const [state, setState] = useState({
    selectedAthletes: [],
    athletesOptions: initialOptions.athletesOptions,
    referAsClients: initialOptions.referAsClients,
    allAthletesFromOrganizationOptions:
      initialOptions.allAthletesFromOrganizationOptions,
    isAllowToViewAllOrganizationsAthletes:
      initialOptions.isAllowToViewAllOrganizationsAthletes,
    isSearching: false,
    showAlreadyInTeamConfirm: false,
    athletesAlreadyInTeam: [],
    athletesOnFlexibleProgram: [],
  });

  useEffect(() => {
    const {
      pendingInvite,
      recipients: pendingRecipients,
      inTeamNames: pendingInTeamNames,
      flexibleProgramNames: pendingFlexibleProgramNames,
    } = query;

    if (pendingInvite && pendingRecipients) {
      setState((prev) => ({
        ...prev,
        showAlreadyInTeamConfirm: true,
        athletesAlreadyInTeam: pendingInTeamNames
          ? pendingInTeamNames.split(",").map((name) => ({ name: name.trim() }))
          : [],
        athletesOnFlexibleProgram: pendingFlexibleProgramNames
          ? pendingFlexibleProgramNames
              .split(",")
              .map((name) => ({ name: name.trim() }))
          : [],
      }));
    }
  }, [query]);

  const {
    selectedAthletes,
    athletesOptions,
    isAllowToViewAllOrganizationsAthletes,
    allAthletesFromOrganizationOptions,
    allAthletes,
    allOrganizationAthletes,
    isSearching,
    showAlreadyInTeamConfirm,
    athletesAlreadyInTeam,
    athletesOnFlexibleProgram,
  } = state;

  const { activeUser: { athletesGroups } = {} } = locals;
  const referAsClients = locals.activeUser?.referAsClients;

  const getFiltersToClear = useCallback(
    (prev) => {
      const isActive =
        prev.allAthletes ||
        prev.allOrganizationAthletes ||
        (athletesGroups?.some((elem) => prev[elem.name]) ?? false);
      if (!isActive) return {};
      const filters = { allAthletes: false, allOrganizationAthletes: false };
      if (athletesGroups) {
        athletesGroups.forEach((elem) => {
          filters[elem.name] = false;
        });
      }
      return filters;
    },
    [athletesGroups]
  );

  const searchAthletes = useCallback((value) => {
    const label = value.toString().toLowerCase();
    setState((prev) => {
      const originalList =
        prev.allAthletesFromOrganizationOptions.length > 0
          ? [...prev.allAthletesFromOrganizationOptions].sort((a, b) =>
              a.label.localeCompare(b.label)
            )
          : prev.athletesOptions;

      const filteredArray = originalList.filter((item) =>
        item.label.toLowerCase().startsWith(label)
      );

      return {
        ...prev,
        athletesOptions: value ? filteredArray : originalList,
      };
    });
  }, []);

  const handleInputChange = useCallback((value, name) => {
    setState((prev) => {
      const {
        selectedAthletes: prevSelected,
        athletesOptions: prevOptions,
        allAthletesFromOrganizationOptions: prevOrgOptions,
        isAllowToViewAllOrganizationsAthletes: prevAllowOrg,
      } = prev;
      let selection = [...prevSelected];

      const currentCoachAthletes =
        prevOrgOptions.length > 0 && prevAllowOrg
          ? prevOrgOptions.filter((athlete) =>
              prevOptions.some((option) => option.value === athlete.value)
            )
          : prevOptions;

      if (name === "allAthletes" && value) {
        selection = currentCoachAthletes;
      } else if (
        (name === "allAthletes" || name === "allOrganizationAthletes") &&
        !value
      ) {
        selection = [];
      } else if (name === "allOrganizationAthletes" && value) {
        selection = prevOrgOptions;
      } else if (value) {
        selection =
          prevOrgOptions.length > 0 && prevAllowOrg
            ? [
                ...selection,
                ...prevOrgOptions.filter((elem) => elem.groups.includes(name)),
              ]
            : [
                ...selection,
                ...prev.athletesOptions.filter((elem) =>
                  elem.groups.includes(name)
                ),
              ];
      } else {
        selection = selection.filter((elem) => !elem.groups.includes(name));
      }

      return {
        ...prev,
        [name]: value,
        selectedAthletes: _.uniq(selection),
        refresh: !prev.refresh,
      };
    });
  }, []);

  const handleAthleteChange = useCallback(
    (recipient) => {
      if (recipient[0] === undefined) {
        setState((prev) => ({
          ...prev,
          selectedAthletes: [],
          ...getFiltersToClear(prev),
        }));
      } else {
        setState((prev) => ({ ...prev, selectedAthletes: recipient }));
      }
    },
    [getFiltersToClear]
  );

  const handleClearAll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedAthletes: [],
      ...getFiltersToClear(prev),
    }));
  }, [getFiltersToClear]);

  const generateAthleteListItem = useCallback(
    (athlete, getImgUrl) => {
      const isSelected = selectedAthletes.some(
        (item) => item.value === athlete.value
      );
      const onClick = () =>
        isSelected
          ? handleAthleteChange(
              selectedAthletes.filter((ath) => ath.value !== athlete.value)
            )
          : handleAthleteChange([...selectedAthletes, athlete]);
      return (
        <li key={`${athlete.value}`} onClick={onClick}>
          <img
            src={getImgUrl(athlete)}
            alt=""
            style={
              getImgUrl(athlete) === "/img/new-icons/user-circle.svg"
                ? {
                    backgroundColor: "var(--background-color)",
                    padding: "8px",
                  }
                : {}
            }
          />
          <span className="athlete-select-label" title={athlete.label}>
            {athlete.label}
          </span>
        </li>
      );
    },
    [selectedAthletes, handleAthleteChange]
  );

  const getImgUrl = (athlete) => {
    if (selectedAthletes.find((selected) => selected.value === athlete.value)) {
      return "/img/new-icons/tick-circle.svg";
    }
    if (athlete.avatar) {
      return `${S3.PATH}${athlete.avatar.s3Path}`;
    }
    return "/img/new-icons/user-circle.svg";
  };

  const cycleId = locals?.params?.cycleId || locals?.editCycle?._id;

  const inviteAthletes = useCallback(() => {
    const recipients = selectedAthletes.map((a) => a.value);
    if (recipients.length === 0) return;

    const cycle = locals?.editCycle;
    const currentDateNumber = Number(moment().format("YYYYMMDD"));
    const program = cycle?.programs?.reduce((prev, prog) => {
      return prog?.startDate <= currentDateNumber &&
        (!prev || prog?.startDate > prev?.startDate)
        ? prog
        : prev;
    }, null);
    const programOrder = program?.order || 1;

    submit(`/actions/cycle/teams-invite-athletes/${cycleId}`, {
      method: "POST",
      body: { recipients, program: programOrder },
    });
  }, [selectedAthletes, locals?.editCycle, cycleId]);

  const submitConfirmMove = useCallback(() => {
    if (!pendingRecipients || !cycleId) return;
    setState((prev) => ({
      ...prev,
      showAlreadyInTeamConfirm: false,
      athletesAlreadyInTeam: [],
    }));
    submit(`/actions/cycle/teams-invite-athletes/${cycleId}`, {
      method: "POST",
      body: {
        recipients: pendingRecipients,
        program: pendingProgram,
        confirmMove: true,
      },
    });
  }, [pendingRecipients, pendingProgram, cycleId]);

  return (
    <Modal
      toClear={[
        "recipients",
        "inTeamNames",
        "flexibleProgramNames",
        "pendingInvite",
      ]}
      className="send-program-modal"
      title={
        referAsClients
          ? t("teams.teamInviteClients.titleClient")
          : t("teams.teamInviteClients.titleAthlete")
      }
    >
      <div className="body">
        <div className="left">
          <div>
            <SelectGroupForDesktop
              handleInputChange={handleInputChange}
              state={state}
              allAthletesFromOrganizationOptions={
                allAthletesFromOrganizationOptions
              }
              isAllowToViewAllOrganizationsAthletes={
                isAllowToViewAllOrganizationsAthletes
              }
              athletesGroups={athletesGroups}
            />
            <SelectGroupForMobile
              handleInputChange={handleInputChange}
              state={state}
              allAthletesFromOrganizationOptions={
                allAthletesFromOrganizationOptions
              }
              isAllowToViewAllOrganizationsAthletes={
                isAllowToViewAllOrganizationsAthletes
              }
              athletesGroups={athletesGroups}
            />
            <SearchAthletesForMobile
              handleInputChange={handleInputChange}
              allAthletes={allAthletes}
              allAthletesFromOrganizationOptions={
                allAthletesFromOrganizationOptions
              }
              allOrganizationAthletes={allOrganizationAthletes}
              searchAthletes={searchAthletes}
              referAsClients={referAsClients}
            />
            <section>
              <header>
                {isSearching ? (
                  <>
                    <input
                      type="search"
                      className="mobile-hidden"
                      name="search"
                      placeholder={t("common:search")}
                      onChange={(e) => searchAthletes(e.target.value)}
                      data-expandable="true"
                    />
                    <SvgIcon
                      role="button"
                      data-button-type="text-white"
                      data-icon-only="true"
                      data-icon
                      sz="small"
                      onClickFn={() => {
                        searchAthletes("");
                        setState((prev) => ({ ...prev, isSearching: false }));
                      }}
                      src="/img/new-icons/close-search.svg"
                    />
                  </>
                ) : (
                  <button
                    type="button"
                    className="p4"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      textAlign: "left",
                      cursor: "pointer",
                      width: "100%",
                    }}
                    onClick={() =>
                      setState((prev) => ({ ...prev, isSearching: true }))
                    }
                  >
                    <span>
                      {referAsClients
                        ? t("teams.teamInviteClients.selectInstructionClients")
                        : t(
                            "teams.teamInviteClients.selectInstructionAthletes"
                          )}
                    </span>
                  </button>
                )}
              </header>

              <ul className="athlete-select">
                {athletesOptions.length > 0 &&
                  athletesOptions.map((athlete) =>
                    generateAthleteListItem(athlete, getImgUrl)
                  )}
              </ul>
            </section>
          </div>
        </div>
        <div className="right">
          <header>
            <h3>
              {referAsClients
                ? t("teams.teamInviteClients.selectedClients")
                : t("teams.teamInviteClients.selectedAthletes")}
            </h3>
            <button
              type="button"
              data-button-type="secondary"
              disabled={selectedAthletes.length === 0}
              onClick={handleClearAll}
            >
              {t("teams.teamInviteClients.clearAll")}
            </button>
          </header>
          <ul className="mb-6">
            <FilterChips
              filters={selectedAthletes.map((athlete) => ({
                text: athlete.label,
                id: athlete.value,
              }))}
              clearFilter={(id) => {
                setState((prev) => ({
                  ...prev,
                  selectedAthletes: prev.selectedAthletes.filter(
                    (athlete) => athlete.value !== id
                  ),
                }));
              }}
            />
          </ul>
        </div>
      </div>

      <footer className="justify-end!">
        <button
          type="button"
          data-button-type="text-white"
          data-action="cancel"
          onClick={() => {
            submit(`/actions/refresh?`, {
              method: "GET",
              body: {
                "modal[type]": "",
              },
            });
          }}
        >
          {t("common:cancel")}
        </button>
        <button
          type="button"
          data-button-type="primary"
          disabled={selectedAthletes.length === 0}
          onClick={inviteAthletes}
        >
          {t("teams.teamInviteClients.invite")}
        </button>
      </footer>

      {showAlreadyInTeamConfirm &&
        (() => {
          // Both groups share one activeTeam mechanism (a Flexible Program
          // is a single 1:1 document, not a team copy - see
          // cycle-teams-check-members.js), so the confirm title names
          // whichever reason(s) actually apply instead of assuming "team"
          // for everyone, same as the per-group list+explanation below.
          const hasInTeam = athletesAlreadyInTeam.length > 0;
          const hasFlexible = athletesOnFlexibleProgram.length > 0;
          const titleKey =
            hasInTeam && hasFlexible
              ? referAsClients
                ? "teams.teamInviteClients.someClientsInTeamOrFlexible"
                : "teams.teamInviteClients.someAthletesInTeamOrFlexible"
              : hasFlexible
                ? referAsClients
                  ? "teams.teamInviteClients.someClientsOnFlexible"
                  : "teams.teamInviteClients.someAthletesOnFlexible"
                : referAsClients
                  ? "teams.teamInviteClients.someClientsInTeam"
                  : "teams.teamInviteClients.someAthletesInTeam";

          return (
            <div
              className="team-invite-confirm-overlay"
              aria-modal="true"
              role="dialog"
              aria-labelledby="team-invite-confirm-title"
            >
              <div className="team-invite-confirm-card confirmation-modal">
                <div className="body pb-0!">
                  <h3 id="team-invite-confirm-title" className="text-center">
                    {t(titleKey)}
                  </h3>
                  <div className="confirm-modal-content rounded-none! border-none! outline-hidden!">
                    {hasInTeam && (
                      <section>
                        <p>
                          {referAsClients
                            ? t(
                                "teams.teamInviteClients.followingClientsInTeam"
                              )
                            : t(
                                "teams.teamInviteClients.followingAthletesInTeam"
                              )}
                        </p>
                        <ul>
                          {athletesAlreadyInTeam.map((a, i) => (
                            <li key={`team-${a.name}-${i}`}>
                              <strong>{a.name}</strong>
                            </li>
                          ))}
                        </ul>
                        <p>{t("teams.teamInviteClients.moveConfirmTeam")}</p>
                      </section>
                    )}
                    {hasFlexible && (
                      <section>
                        <p>
                          {referAsClients
                            ? t(
                                "teams.teamInviteClients.followingClientsOnFlexible"
                              )
                            : t(
                                "teams.teamInviteClients.followingAthletesOnFlexible"
                              )}
                        </p>
                        <ul>
                          {athletesOnFlexibleProgram.map((a, i) => (
                            <li key={`flex-${a.name}-${i}`}>
                              <strong>{a.name}</strong>
                            </li>
                          ))}
                        </ul>
                        <p>
                          {t("teams.teamInviteClients.moveConfirmFlexible")}
                        </p>
                      </section>
                    )}
                    <p>{t("teams.teamInviteClients.proceedConfirm")}</p>
                  </div>
                </div>
                <footer id="confirmation-modal-footer">
                  <button
                    type="button"
                    data-button-type="secondary"
                    onClick={() => {
                      setState((prev) => ({
                        ...prev,
                        showAlreadyInTeamConfirm: false,
                        athletesAlreadyInTeam: [],
                        athletesOnFlexibleProgram: [],
                      }));
                      submit(`/actions/refresh?`, {
                        method: "GET",
                        body: {
                          "modal[type]": "team-invite-clients",
                          pendingInvite: "",
                          recipients: "",
                          inTeamNames: "",
                          flexibleProgramNames: "",
                        },
                      });
                    }}
                  >
                    <span>{t("common:cancel")}</span>
                  </button>
                  <button
                    type="button"
                    data-button-type="primary"
                    onClick={submitConfirmMove}
                  >
                    {t("teams.teamInviteClients.proceed")}
                  </button>
                </footer>
              </div>
            </div>
          );
        })()}
    </Modal>
  );
}

export default TeamInviteClients;

const SelectGroupForDesktop = ({
  handleInputChange,
  state,
  allAthletesFromOrganizationOptions,
  isAllowToViewAllOrganizationsAthletes,
  athletesGroups,
}) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <fieldset className="select-by-group">
      <legend className="select-title">
        {t("teams.teamInviteClients.selectByGroup")}
      </legend>
      <div className="select-group-filters">
        <div
          className="select-group-filter"
          onClick={() => handleInputChange(!state.allAthletes, "allAthletes")}
        >
          <img
            src={`/img/new-icons/${
              state.allAthletes ? "checked" : "unchecked"
            }-square.svg`}
            alt=""
          />
          {state.referAsClients
            ? t("teams.teamInviteClients.allClients")
            : t("teams.teamInviteClients.allAthletes")}
        </div>
        {allAthletesFromOrganizationOptions?.length > 0 &&
          isAllowToViewAllOrganizationsAthletes && (
            <div
              className="select-group-filter"
              onClick={() =>
                handleInputChange(
                  !state.allOrganizationAthletes,
                  "allOrganizationAthletes"
                )
              }
            >
              <img
                src={`/img/new-icons/${
                  state.allOrganizationAthletes ? "checked" : "unchecked"
                }-square.svg`}
                alt=""
              />
              {state.referAsClients
                ? t("teams.teamInviteClients.allOrganizationClients")
                : t("teams.teamInviteClients.allOrganizationAthletes")}
            </div>
          )}
        {athletesGroups?.map((elem) => (
          <div
            key={elem.name}
            className="select-group-filter"
            onClick={() => handleInputChange(!state[elem.name], elem.name)}
          >
            <img
              src={`/img/new-icons/${
                state[elem.name] ? "checked" : "unchecked"
              }-square.svg`}
              alt=""
            />
            <span className="select-group-filter-label" title={elem.name}>
              {elem.name}
            </span>
          </div>
        ))}
      </div>
    </fieldset>
  );
};

const SelectGroupForMobile = ({
  handleInputChange,
  state,
  allAthletesFromOrganizationOptions,
  isAllowToViewAllOrganizationsAthletes,
  athletesGroups,
}) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <div className="select-by-group-mobile force-only-show-mobile">
      <div
        className={
          state.allAthletes
            ? "select-group-filter-mobile active-select"
            : "select-group-filter-mobile"
        }
        onClick={() => handleInputChange(!state.allAthletes, "allAthletes")}
      >
        {state.referAsClients
          ? t("teams.teamInviteClients.allClients")
          : t("teams.teamInviteClients.allAthletes")}
      </div>
      {allAthletesFromOrganizationOptions.length > 0 &&
        isAllowToViewAllOrganizationsAthletes && (
          <div
            className={
              state.allOrganizationAthletes
                ? "select-group-filter-mobile active-select"
                : "select-group-filter-mobile"
            }
            onClick={() =>
              handleInputChange(
                !state.allOrganizationAthletes,
                "allOrganizationAthletes"
              )
            }
          >
            {state.referAsClients
              ? t("teams.teamInviteClients.allOrganizationClients")
              : t("teams.teamInviteClients.allOrganizationAthletes")}
          </div>
        )}
      {athletesGroups?.map((elem) => (
        <div
          key={elem.name}
          className={
            state[elem.name]
              ? "select-group-filter-mobile active-select"
              : "select-group-filter-mobile"
          }
          onClick={() => handleInputChange(!state[elem.name], elem.name)}
        >
          {elem.name}
        </div>
      ))}
    </div>
  );
};

const SearchAthletesForMobile = ({
  handleInputChange,
  allAthletes,
  allAthletesFromOrganizationOptions,
  allOrganizationAthletes,
  searchAthletes,
  referAsClients,
}) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const isAllAthletesFromOrganizationOptionsEmpty =
    (allAthletesFromOrganizationOptions?.length ?? 0) === 0;

  const handleClick = () => {
    if (isAllAthletesFromOrganizationOptionsEmpty) {
      handleInputChange(!allAthletes, "allAthletes");
    } else {
      handleInputChange(!allOrganizationAthletes, "allOrganizationAthletes");
    }
  };

  const athletesToSelect = isAllAthletesFromOrganizationOptionsEmpty
    ? allAthletes
    : allOrganizationAthletes;

  return (
    <div className="force-only-show-mobile" id="search-athlete-select">
      <input
        name="search"
        type="search"
        onChange={(e) => searchAthletes(e.target.value)}
        placeholder={
          referAsClients
            ? t("teams.teamInviteClients.searchPlaceholderClient")
            : t("teams.teamInviteClients.searchPlaceholderAthlete")
        }
        autoComplete="off"
      />
      <button type="button" onClick={handleClick}>
        <img
          src={
            athletesToSelect
              ? "/img/new-icons/checked-square.svg"
              : "/img/new-icons/unchecked-square.svg"
          }
          alt=""
        />
        <span>{t("teams.teamInviteClients.selectAll")}</span>
      </button>
    </div>
  );
};
